// Shared by the manager and partner permission upload flows, which post to two
// endpoints that answer with the same status codes and the same error body.
//
// `upload()` in apiClient is a bare fetch and fetch does not reject on 4xx/5xx,
// so callers must inspect the status themselves or every failure is reported to
// the user as a successful upload.

const validationReasons: { [type: string]: string } = {
    'non-ascii-file-name': 'its name may only contain plain Latin letters, digits and basic punctuation',
    'empty-file': 'the file is empty',
    'no-file': 'no file was attached',
    'unsupported-file-type': 'its type is not supported (only PDF, JPG and PNG are allowed)',
};

interface UploadErrorBody {
    type?: string;
}

async function readErrorType(response: Response): Promise<string> {
    try {
        const body: UploadErrorBody = (await response.json()) ?? {};
        return body.type ?? '';
    } catch {
        return '';
    }
}

export const uploadSuccessMessage = (fileName: string): string => `${fileName} was uploaded successfully`;

export const uploadErrorMessage = (fileName: string): string => `${fileName} could not be uploaded`;

export async function uploadFailureMessage(response: Response, fileName: string): Promise<string> {
    if (response.status === 413) {
        return `${fileName} could not be uploaded because it is too large`;
    }

    if (response.status === 502) {
        return `${fileName} could not be uploaded right now, please try again`;
    }

    // Only messages we author are shown; the raw server message is never echoed,
    // so framework wording (e.g. a 400 from ParseIntPipe) cannot leak to the user.
    const reason = validationReasons[await readErrorType(response)];

    return reason ? `${fileName} could not be uploaded because ${reason}` : uploadErrorMessage(fileName);
}
